import { useEffect, useMemo } from 'react'
import { Dialog, DialogContent } from '@mui/material'
import { WasteContainerDto, WasteContainerMeasurementDto, WasteMeasurementType } from '@services/dtos'
import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import moment from 'moment'
import { FormHeader } from '../forms/FormHeader'
import { getContainerSubTitles, getNameWithRole } from '@utils/waste.utils'
import { UpTransition } from '../animations'
import { HotLabEquipmentTypeLabels } from '@utils/constants'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { getWasteContainerMeasurements, setWasteContainerMeasurementsFilters } from '@state/reducers/wasteContainerReducer'

const PAGE_SIZE = 20

const MeasurementTypeLabels: Record<WasteMeasurementType, string> = {
  [WasteMeasurementType.CLOSE]: 'Fermeture',
  [WasteMeasurementType.DISPOSAL]: 'Élimination',
  [WasteMeasurementType.PERIODIC]: 'Périodique',
}

type Props = {
  container: WasteContainerDto
  onClose: () => void
}

export function WasteContainerMeasurementsDialog({ container, onClose }: Props) {
  const dispatch = useAppDispatch()
  const { measurements, measurementsLoading, measurementsFilters } = useAppSelector(
    (state) => state.wasteContainer,
  )

  useEffect(() => {
    dispatch(setWasteContainerMeasurementsFilters({ containerId: container.id, page: 0, limit: PAGE_SIZE }))
    dispatch(getWasteContainerMeasurements())
    return () => {
      dispatch(setWasteContainerMeasurementsFilters({ containerId: undefined }))
    }
  }, [container.id])

  const columns: GridColumns<WasteContainerMeasurementDto> = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: 'Date',
        width: 145,
        valueFormatter: ({ value }) => moment(value).format('DD/MM/YYYY HH:mm'),
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 120,
        valueFormatter: ({ value }) => MeasurementTypeLabels[value as WasteMeasurementType] ?? value,
      },
      {
        field: 'hotLabEquipment',
        headerName: 'Appareil',
        width: 240,
        valueFormatter: ({ value }) => value ? `${value.name} - ${HotLabEquipmentTypeLabels[value.type] ?? value.type}` : '',
      },
      {
        field: 'measurementCps',
        headerName: 'Mesure (cps)',
        width: 105,
        type: 'number',
      },
      {
        field: 'backgroundNoiseCps',
        headerName: 'Bruit de fond (cps)',
        width: 145,
        type: 'number',
      },
      {
        field: 'distanceM',
        headerName: 'Distance (m)',
        width: 105,
        type: 'number',
      },
      {
        field: 'createdBy',
        headerName: 'Saisi par',
        width: 180,
        valueFormatter: ({ value }) => value ? getNameWithRole(value) : '',
      },
      {
        field: 'measuredBy',
        headerName: 'Mesuré par',
        width: 180,
        valueFormatter: ({ value }) => value ? getNameWithRole(value) : '',
      },
    ],
    [],
  )

  return (
    <Dialog
      open
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { height: '70vh' } }}
      TransitionComponent={UpTransition}
      onClose={onClose}
    >
      <div className="px-12 pt-6">
        <FormHeader
          title={`Mesures #${container.no}`}
          subTitles={getContainerSubTitles(container)}
          onClose={onClose}
          isSubmitting={false}
          showSubmit={false}
        />
      </div>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', pt: 1, overflow: 'hidden' }}>
        <DataGridPro
          density="compact"
          pagination
          paginationMode="server"
          page={measurementsFilters?.page ?? 0}
          pageSize={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(page) => {
            dispatch(setWasteContainerMeasurementsFilters({ page }))
            dispatch(getWasteContainerMeasurements())
          }}
          rows={measurements?.datas ?? []}
          rowCount={measurements?.totalCount ?? 0}
          columns={columns}
          loading={measurementsLoading ?? false}
          disableSelectionOnClick
          disableMultipleSelection
          sx={{ backgroundColor: 'white', borderRadius: '15px 15px 25px 25px' }}
        />
      </DialogContent>
    </Dialog>
  )
}
